/** Editor dialogs: page settings / new page, theme, AI, publish, accurate preview, custom component. */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Collapse, ColorPicker, Form, Input, List, Modal, Result, Segmented, Select, Slider, Space, Spin, Switch, Typography } from 'antd';
import { CloseOutlined, RobotOutlined, UndoOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import { AiCreditBadge, progressText, reportAiError, runAi } from '../ai';
import { toastErr } from '../common';
import { DEFAULT_THEME, FONT_STACKS } from './engine';

const { Text, Paragraph } = Typography;

export const PAGE_TYPES = [
    { value: 'page', label: 'Page', path: '' },
    { value: 'product', label: 'Product', path: '/products/:handle' },
    { value: 'collection', label: 'Collection', path: '/collections/:handle' },
    { value: 'blog', label: 'Blog', path: '/blog' },
    { value: 'post', label: 'Blog post', path: '/blog/:slug' },
    { value: 'search', label: 'Search', path: '/search' },
    { value: 'cart', label: 'Cart', path: '/cart' },
    { value: 'account', label: 'Account', path: '/account' },
    { value: 'order', label: 'Order status', path: '/orders/:token' },
    { value: '404', label: 'Not found (404)', path: '/404' },
];

const pathify = (s) => `/${String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;

/** page: existing page (settings) or null (new page). onSubmit(values) */
export const PageSettingsModal = ({ open, page, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [pathTouched, setPathTouched] = useState(false);
    useEffect(() => {
        if (!open) return;
        setPathTouched(!!page);
        form.setFieldsValue(page
            ? { title: page.title, path: page.path, type: page.type || 'page', description: page.description || '', image: page.image || '', noindex: !!page.noindex, lang: page.lang || '', layout: page.layout || 'default' }
            : { title: '', path: '/', type: 'page', description: '', image: '', noindex: false, lang: '' });
    }, [open, page, form]);
    const submit = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try { await onSubmit(v); } finally { setSaving(false); }
    };
    return (
        <Modal open={open} title={page ? t('Page settings') : t('New page')} onCancel={onClose} onOk={submit} confirmLoading={saving} okText={page ? t('Save') : t('Create')} destroyOnHidden>
            <Form form={form} layout="vertical" requiredMark={false}
                onValuesChange={(ch) => {
                    if ('title' in ch && !pathTouched && form.getFieldValue('type') === 'page') form.setFieldsValue({ path: ch.title ? pathify(ch.title) : '/' });
                    if ('type' in ch) {
                        const tp = PAGE_TYPES.find((x) => x.value === ch.type);
                        if (tp?.path) form.setFieldsValue({ path: tp.path });
                    }
                    if ('path' in ch) setPathTouched(true);
                }}>
                <Form.Item name="title" label={t('Title')} rules={[{ required: true, message: t('Enter a title') }]}><Input autoFocus maxLength={255} /></Form.Item>
                <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="type" label={t('Type')} style={{ width: 170 }}>
                        <Select options={PAGE_TYPES.map((x) => ({ value: x.value, label: t(x.label) }))} />
                    </Form.Item>
                    <Form.Item name="path" label={t('Path')} style={{ flex: 1 }}
                        rules={[{ required: true }, { pattern: /^\/[A-Za-z0-9._~%/:-]*$/, message: t('A path starts with / and has no spaces') }]}>
                        <Input />
                    </Form.Item>
                </Space.Compact>
                {page && (
                    <Form.Item name="layout" label={t('Use site header and footer')} getValueProps={(v) => ({ checked: v !== 'none' })} normalize={(on) => (on ? 'default' : 'none')}>
                        <Switch />
                    </Form.Item>
                )}
                <Collapse ghost size="small" items={[{
                    key: 'seo',
                    label: t('SEO'),
                    children: (
                        <>
                            <Form.Item name="description" label={t('Meta description')}><Input.TextArea maxLength={500} showCount autoSize={{ minRows: 2 }} /></Form.Item>
                            <Form.Item name="image" label={t('Share image URL')}><Input placeholder="https://…" /></Form.Item>
                            <Form.Item name="lang" label={t('Language')} tooltip={t('Empty = the site language')}><Input placeholder="en" style={{ width: 120 }} /></Form.Item>
                            <Form.Item name="noindex" valuePropName="checked"><Checkbox>{t('Hide from search engines')}</Checkbox></Form.Item>
                        </>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

export const ThemeModal = ({ open, theme, onChange, onClose, onSave, saving }) => {
    const { t } = useTranslation();
    const th = { ...DEFAULT_THEME, ...(theme || {}) };
    const set = (patch) => onChange({ ...th, ...patch });
    const color = (k, label) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <Text>{label}</Text>
            <ColorPicker value={th[k]} showText disabledAlpha onChange={(c) => set({ [k]: c.toHexString() })} />
        </div>
    );
    return (
        <Modal open={open} title={t('Theme')} onCancel={onClose} onOk={onSave} confirmLoading={saving} okText={t('Save')} width={420} mask={false}>
            {color('accent', t('Accent'))}
            {color('background', t('Background'))}
            {color('text', t('Text'))}
            <div style={{ marginBottom: 12 }}>
                <Text>{t('Font')}</Text>
                <Segmented block value={th.font} onChange={(font) => set({ font })} style={{ marginTop: 6 }}
                    options={Object.keys(FONT_STACKS).map((k) => ({ value: k, label: <span style={{ fontFamily: FONT_STACKS[k] }}>{t({ system: 'Sans', serif: 'Serif', rounded: 'Rounded', mono: 'Mono' }[k])}</span> }))} />
            </div>
            <div>
                <Text>{t('Corner radius')}</Text>
                <Slider min={0} max={40} value={Number(th.radius) || 0} onChange={(radius) => set({ radius })} />
            </div>
        </Modal>
    );
};

const AI_MODES = {
    page: { title: 'Generate this page with AI', ph: 'Describe the page, e.g. "A landing page for a coffee shop with menu, opening hours and a contact form"' },
    section: { title: 'Add a section with AI', ph: 'Describe the section, e.g. "Three pricing plans with a monthly/yearly note"' },
    copy: { title: 'Rewrite with AI', ph: 'How should the text change? e.g. "Shorter and friendlier"' },
    block: { title: 'Edit this block with AI', ph: 'What should change? e.g. "Title on the right, three columns, hide the count"' },
    component: { title: 'New component with AI', ph: 'Describe the block, e.g. "A team grid with photo, name, role and social links"' },
};

/** mode: page | section | copy | block | component; body extras (page_id, instance_id, after_instance_id) via `extra` */
export const AiModal = ({ open, siteId, mode = 'page', extra, onClose, onDone }) => {
    const { t } = useTranslation();
    const [prompt, setPrompt] = useState('');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState('');
    const [soon, setSoon] = useState(false);
    const [credit, setCredit] = useState(0);
    useEffect(() => { if (open) { setSoon(false); setStatus(''); } }, [open]);
    const m = AI_MODES[mode] || AI_MODES.page;
    const run = async () => {
        if (!prompt.trim()) return;
        setBusy(true);
        try {
            const res = await runAi(siteId, mode, { prompt: prompt.trim(), ...(extra || {}) }, { onStatus: (st, job) => setStatus(st === 'running' ? (progressText(job?.result?.progress) || st) : (st || '')) });
            setPrompt('');
            onDone(res, mode);
        } catch (e) {
            if (reportAiError(e)) setSoon(true);
        } finally {
            setBusy(false);
            setStatus('');
            setCredit((x) => x + 1);
        }
    };
    return (
        <Modal open={open} title={<Space><RobotOutlined />{t(m.title)}<AiCreditBadge refreshKey={credit} /></Space>} onCancel={busy ? undefined : onClose} footer={soon ? null : undefined}
            onOk={run} okText={busy ? (status && status !== 'running' && status !== 'queued' ? status : t(status === 'running' ? 'Writing…' : 'Queued…')) : t('Generate')} confirmLoading={busy} okButtonProps={{ disabled: !prompt.trim() }} maskClosable={!busy}>
            {soon ? (
                <Result icon={<RobotOutlined />} title={t('AI generation is coming soon')} extra={<Button onClick={onClose}>{t('OK')}</Button>} />
            ) : (
                <Input.TextArea autoFocus value={prompt} onChange={(e) => setPrompt(e.target.value)} autoSize={{ minRows: 4, maxRows: 10 }} maxLength={4000}
                    disabled={busy} placeholder={t(m.ph)} onPressEnter={(e) => { if (e.ctrlKey || e.metaKey) run(); }} />
            )}
        </Modal>
    );
};

export const PublishModal = ({ open, onClose, onPublish, busy, problems }) => {
    const { t } = useTranslation();
    const [note, setNote] = useState('');
    useEffect(() => { if (open) setNote(''); }, [open]);
    return (
        <Modal open={open} title={t('Publish')} onCancel={onClose} onOk={() => onPublish(note)} confirmLoading={busy} okText={t('Publish')}>
            {problems?.length ? <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('{{n}} problems on this page', { n: problems.length })} /> : null}
            <Input placeholder={t('Note (optional)')} value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} onPressEnter={() => onPublish(note)} />
        </Modal>
    );
};

const DEVICE_W = { desktop: '100%', tablet: 820, mobile: 390 };
export const AccuratePreviewModal = ({ open, onClose, loading, result, device }) => {
    const { t } = useTranslation();
    const problems = result?.problems || [];
    const missing = result?.missing_components || [];
    return (
        <Modal open={open} onCancel={onClose} footer={null} width="94vw" style={{ top: 20 }} title={t('Accurate preview')} destroyOnHidden
            styles={{ body: { height: '80vh', display: 'flex', flexDirection: 'column', gap: 8 } }}>
            {(problems.length > 0 || missing.length > 0) && (
                <Alert type="warning" showIcon message={t('{{n}} problems', { n: problems.length + missing.length })}
                    description={<div style={{ maxHeight: 90, overflow: 'auto', fontSize: 12 }}>
                        {missing.map((m, i) => <div key={`m${i}`}>{t('Missing component')}: {typeof m === 'string' ? m : JSON.stringify(m)}</div>)}
                        {problems.map((p, i) => <div key={i}><Text code>{p.path}</Text> {p.message}</div>)}
                    </div>} />
            )}
            <div style={{ flex: 1, background: '#e5e7eb', display: 'flex', justifyContent: 'center', borderRadius: 6, overflow: 'hidden' }}>
                {loading || !result ? <Spin style={{ alignSelf: 'center' }} />
                    : <iframe title="preview" sandbox="allow-scripts" srcDoc={result.html} style={{ width: DEVICE_W[device] || '100%', maxWidth: '100%', height: '100%', border: 0, background: '#fff' }} />}
            </div>
        </Modal>
    );
};

const SAMPLE_COMPONENT = `{
  "name": "MyBanner",
  "label": "My banner",
  "category": "banner",
  "description": "A simple banner",
  "fields": {
    "text": { "type": "text", "label": "Text", "default": "Hello" }
  },
  "template": "<div class=\\"b\\">{{ text }}</div>",
  "css": ".b{padding:24px;background:var(--yc-accent);color:#fff;text-align:center}"
}`;

export const CustomComponentModal = ({ open, api, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [src, setSrc] = useState(SAMPLE_COMPONENT);
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState(null);
    useEffect(() => { if (open) setReport(null); }, [open]);
    const save = async () => {
        let def;
        try { def = JSON.parse(src); } catch (e) { setReport({ ok: false, report: { error: t('Invalid JSON: {{msg}}', { msg: e.message }) } }); return; }
        setBusy(true);
        try {
            const r = await api.post('/components', { definition: def });
            setReport(r);
            if (r?.ok) onSaved(r);
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    const rep = report?.report || {};
    const problems = rep.problems || rep.errors || [];
    return (
        <Modal open={open} title={t('Custom component')} onCancel={onClose} onOk={save} confirmLoading={busy} okText={t('Validate and save')} width={760}>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>{t('JSON with fields, template (Liquid), css. Saved to your library after validation.')}</Paragraph>
            <Input.TextArea value={src} onChange={(e) => setSrc(e.target.value)} autoSize={{ minRows: 14, maxRows: 26 }} style={{ fontFamily: 'monospace', fontSize: 12 }} spellCheck={false} />
            {report && (
                <Alert style={{ marginTop: 12 }} type={report.ok ? 'success' : 'error'} showIcon
                    message={report.ok ? t('Saved as {{name}} v{{v}}', { name: report.name, v: report.version }) : (rep.error || t('Validation failed'))}
                    description={problems.length ? <List size="small" dataSource={problems} renderItem={(p) => <List.Item style={{ padding: '2px 0' }}><Text code>{p.path}</Text> {p.message}</List.Item>} /> : null} />
            )}
        </Modal>
    );
};

const shortVal = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'string') return v.length > 40 ? `${v.slice(0, 40)}…` : v;
    const j = JSON.stringify(v);
    return j.length > 40 ? `${j.slice(0, 40)}…` : j;
};

/** Result of an AI block edit: summary, changed fields and Undo (restores the previous instance). */
export const AiBlockResult = ({ result, onUndo, onClose, undoing }) => {
    const { t } = useTranslation();
    if (!result) return null;
    const changes = Array.isArray(result.changes) ? result.changes : [];
    const ok = !!result.changed;
    return (
        <Alert style={{ marginBottom: 10 }} type={ok ? 'success' : (result.action === 'impossible' ? 'warning' : 'info')} showIcon icon={<RobotOutlined />}
            message={<div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}><span style={{ flex: 1, fontSize: 13 }}>{result.summary || (ok ? t('Done') : t('Nothing was changed'))}</span>
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={onClose} /></div>}
            description={(changes.length > 0 || result.component_created || ok) && (
                <div style={{ fontSize: 12 }}>
                    {result.component_created && <div><Text type="secondary">{t('New component')}:</Text> <Text code>{result.component_created}</Text></div>}
                    {changes.filter((c) => c.field !== '(component)').slice(0, 8).map((c, i) => (
                        <div key={i}><Text code>{c.label || c.field}</Text> <Text type="secondary">{shortVal(c.from)}</Text> → {shortVal(c.to)}</div>
                    ))}
                    {ok && result.undo && <Button size="small" icon={<UndoOutlined />} style={{ marginTop: 6 }} loading={undoing} onClick={onUndo}>{t('Undo')}</Button>}
                </div>
            )} />
    );
};
